import "firebase/compat/auth";

import queryString from "querystring";

import { trackEvent } from "@glide/common-core/dist/js/analytics";
import { getFeatureSettingStable } from "@glide/common-core/dist/js/feature-settings";
import type {
    GetOAuth2TokensForGoogleSheetsBody,
    IsEmailSignedUpBody,
    UserDataUpdates,
} from "@glide/common-core/dist/js/firebase-function-types";
import * as routes from "@glide/common-core/dist/js/routes";
import { getAppFacilities } from "@glide/common-core/dist/js/support/app-renderer";
import { initFirebase, setLastFirebaseAuthFailure } from "@glide/firebase-stuff";
import { callCloudFunctionWeb } from "@glide/common-core/dist/js/utility/function-utils";
import { exceptionToString, panic } from "@glideapps/ts-necessities";
import { checkString, isResponseOK, isRunningOnEmulators, logError, logInfo, nullToUndefined } from "@glide/support";
import { hasOwnProperty } from "collection-utils";
import firebase from "firebase/compat/app";
import { saveUserUpdates } from "@glide/backend-api";
import { getAuth2, googleDriveScope, setGAPIAccessToken } from "./google-api";
import { forceIndexedDBReplacementIfNecessary } from "@glide/indexeddb-hacks";
import { getSetOrUpdateUsersAppParams } from "@glide/common-core/dist/js/get-set-or-update-user-app-params";

export interface AuthenticatedUser {
    firebaseUser: firebase.User;
    googleAccessToken: string | undefined;
}

export function isAuthenticatedUser(x: unknown): x is AuthenticatedUser {
    return hasOwnProperty(x, "firebaseUser");
}

let authenticatedUser: AuthenticatedUser | undefined | "loading" = "loading";

let didInit = false;
// FIXME: We're calling this function everywhere "just in case".
// It sounds like a smell of using global _stateful_ things (like firebase)
function init(): void {
    if (didInit) return;

    initFirebase();
    forceIndexedDBReplacementIfNecessary(window);

    firebase.auth().onAuthStateChanged(authStateChanged, e => {
        setLastFirebaseAuthFailure(exceptionToString(e));
        logError("Firebase auth failed", e);
    });

    didInit = true;
}

type AuthStateChangedCallback = (u: firebase.User | undefined) => void;

const callbacks: AuthStateChangedCallback[] = [];
let callbacksEnabled = true;
let disableUid = "";
export function disableCallbacks() {
    if (!callbacksEnabled) return;
    disableUid = firebase.auth().currentUser?.uid ?? "";
    callbacksEnabled = false;
}

export function enableCallbacks() {
    callbacksEnabled = true;
    // @ts-expect-error FIXME: this is most likely a bug
    if (disableUid === firebase.auth().currentUser?.uid ?? "") return;

    for (const callback of callbacks) {
        try {
            callback(firebase.auth().currentUser ?? undefined);
        } catch (e: unknown) {
            logError("Error calling auth state changed callback", e);
        }
    }
}

export function isFirebaseUserSignedIn(): boolean {
    return authenticatedUser !== undefined && authenticatedUser !== "loading";
}

export function onAuthStateChanged(callback: AuthStateChangedCallback): void {
    init();

    /**
     * We need to push the callback to the global array _before_ calling it
     * so the callback can remove itself from the array.
     *
     * Example:
     *
     * const callback = () => {
     *   removeOnAuthStateChanged(callback);
     *   // Do whatever...
     * }
     * onAuthStateChanged(callback);
     *
     * This callback should only run once.
     */
    callbacks.push(callback);

    // We trigger the provided auth callback immediately if we have a valid
    // auth state for one particular use case: we want to avoid popping login
    // screens if we can. In order to do this, we have to be _certain_ that
    // we're not logged in.
    //
    // The default state, before Firebase Auth completes, is "loading", but we
    // don't have any control over how long this process takes. It may be the
    // case, when we want to establish the auth callback, that we already are
    // certain of our auth state. So, if we are, we immediately fire the
    // provided callback.
    if (authenticatedUser !== "loading" && callbacksEnabled) {
        callback(authenticatedUser?.firebaseUser);
    }
}

export function removeOnAuthStateChanged(callback: AuthStateChangedCallback): void {
    const removeIndex = callbacks.indexOf(callback);
    if (removeIndex < 0) return;

    callbacks.splice(removeIndex, 1);
}

export async function setAuthenticatedUser(user: AuthenticatedUser | undefined): Promise<void> {
    const stateChanged =
        authenticatedUser === "loading" || user?.firebaseUser.uid !== authenticatedUser?.firebaseUser.uid;

    authenticatedUser = user;

    if (
        !(await getFeatureSettingStable("useGoogleIdentityServices")) &&
        user !== undefined &&
        user.googleAccessToken !== undefined
    ) {
        await setGAPIAccessToken(user.googleAccessToken);
    }

    if (!stateChanged) return;
    if (!callbacksEnabled) return;

    for (const callback of callbacks) {
        try {
            callback(user?.firebaseUser);
        } catch (e: unknown) {
            logError("Error calling auth state changed callback", e);
        }
    }
}

interface GetAccessTokenSuccessfulResponse {
    error: false;
    accessToken: string;
}

// There are many possible reasons for this to fail, but we only check for either
// the 403 case, or any other (formerly 403 returned `true` and any other error `false`).
// Here, a forbidden response means the user revoked permissions for glide.
type GetAccessTokenFailureReason = "forbidden" | "generic";

interface GetAccessTokenFailedResponse {
    error: true;
    reason: GetAccessTokenFailureReason;
}

type GetAccessTokenResponse = GetAccessTokenSuccessfulResponse | GetAccessTokenFailedResponse;

function accessTokenError(reason: GetAccessTokenFailureReason): GetAccessTokenResponse {
    return {
        error: true,
        reason,
    };
}

function accessTokenSuccess(accessToken: string): GetAccessTokenResponse {
    return {
        error: false,
        accessToken,
    };
}

function isSuccessAccessTokenResponse(response: unknown): response is GetAccessTokenSuccessfulResponse {
    if (typeof response === "object" && response !== null) {
        const responseAsSuccess = response as GetAccessTokenSuccessfulResponse;

        return responseAsSuccess.error === false && typeof responseAsSuccess.accessToken === "string";
    }

    return false;
}

export interface MergeWithExistingUser {
    readonly existingUserIDToken: string;
    readonly newUserAccessCode: string;
}

async function getAccessTokenForCurrentFirebaseUser(
    mergeWith: MergeWithExistingUser | undefined
): Promise<GetAccessTokenResponse> {
    if (!(await getFeatureSettingStable("useGoogleIdentityServices"))) {
        let auth2: Omit<gapi.auth2.GoogleAuth, "then"> | undefined;
        try {
            auth2 = await getAuth2();
        } catch {
            return accessTokenError("generic");
        }
        logInfo("Got auth2");
        if (auth2 === undefined) return accessTokenError("generic");
        // You can be signed out of Google but still get valid access tokens.
        // The access tokens will be useless, because you aren't signed in.
        // We'll just bail if you aren't signed in because you wouldn't be
        // able to use the result anyway.
        if (!(auth2 as gapi.auth2.GoogleAuth).isSignedIn.get()) return accessTokenError("generic");
    }

    const requestBody: GetOAuth2TokensForGoogleSheetsBody = {
        mergeWithFirebaseIDToken: mergeWith?.existingUserIDToken,
        code: mergeWith?.newUserAccessCode,
    };
    const response = await getAppFacilities().callAuthCloudFunction("getOAuth2TokensForGoogleSheets", requestBody);
    if (response === undefined) return accessTokenError("generic");
    if (response.status === 403) {
        // We have to drain out the body, otherwise we'll just leave the connection
        // around forever.
        void response?.text();
        return accessTokenError("forbidden");
    }
    if (!response.ok) {
        // We have to drain out the body, otherwise we'll just leave the connection
        // around forever.
        void response.text();
        logError("No access token from the server");
        return accessTokenError("generic");
    }

    const body = await response.json();
    if (!hasOwnProperty(body, "accessToken")) {
        return panic("Response to getAccessToken not recognized");
    }

    const accessToken = checkString(body.accessToken);

    return accessTokenSuccess(accessToken);
}

let gotFirstAuthStateChanged = false;

async function authStateChanged(firebaseUser: firebase.User | null): Promise<void> {
    if (!callbacksEnabled) return;

    gotFirstAuthStateChanged = true;

    if (firebaseUser === null) {
        logInfo("Not logged in");
        return setAuthenticatedUser(undefined);
    }

    // FIXME: This is pure cruft.
    // We used to be able to rely on Google auth not getting incessantly signed out
    // but on Safari with Intelligent Tracking Prevention enabled, we're always
    // signed out of Google as soon as we open the page.
    // We need to not rely so much on the googleAccessToken being around
    // in this particular codepath.
    let maybeGoogleAccessToken: GetAccessTokenResponse | undefined;
    if (!isRunningOnEmulators() && !routes.isPlayer()) {
        maybeGoogleAccessToken = await getAccessTokenForCurrentFirebaseUser(undefined);
    }

    const googleAccessToken = isSuccessAccessTokenResponse(maybeGoogleAccessToken)
        ? maybeGoogleAccessToken.accessToken
        : undefined;

    return setAuthenticatedUser({ firebaseUser, googleAccessToken });
}

export async function tryGetAccessTokenAndAuthenticate(): Promise<AuthenticatedUser | undefined> {
    if (authenticatedUser === undefined || authenticatedUser === "loading") return undefined;

    const maybeGoogleAccessToken = await getAccessTokenForCurrentFirebaseUser(undefined);

    const googleAccessToken = isSuccessAccessTokenResponse(maybeGoogleAccessToken)
        ? maybeGoogleAccessToken.accessToken
        : undefined;

    const user = { ...authenticatedUser, googleAccessToken };

    // Defaulting to `null` so we don't have a `user | null | undefined`
    const googleProvider =
        authenticatedUser.firebaseUser.providerData.find(p => p?.providerId === "google.com") ?? null;

    if (googleProvider !== null && googleAccessToken === undefined) {
        return undefined;
    }
    await setAuthenticatedUser(user);
    return user;
}

type AuthenticationFailureKind =
    | "declined-sign-in"
    | "popup-blocked"
    | "no-google-libraries"
    | "cookies-blocked"
    | "link-expired"
    | "unexpected-error";

export interface AuthenticationFailure {
    kind: AuthenticationFailureKind;
    message?: string;
}

interface GetGoogleUserAlreadySignedIn {
    error: false;
    alreadySignedIn: true;
    user: AuthenticatedUser;
}

interface GetGoogleUserSuccessResponse {
    error: false;
    alreadySignedIn: false;
    user: gapi.auth2.GoogleUser;
    code: string | undefined;
}

interface GetGoogleUserSuccessResponseV2 {
    error: false;
    alreadySignedIn: false;
}

export function isGoogleUserError(
    response:
        | GetGoogleUserSuccessResponse
        | GetGoogleUserSuccessResponseV2
        | AuthenticationFailure
        | GetGoogleUserAlreadySignedIn
): response is AuthenticationFailure {
    return (response as AuthenticationFailure).kind !== undefined;
}

// DOES NOT THROW! Returns errors specified above.
export async function getGoogleUserForSignIn(
    grantOfflineAccess: boolean
): Promise<GetGoogleUserSuccessResponse | AuthenticationFailure | GetGoogleUserAlreadySignedIn> {
    // Not sure if we have to `init` here. This global dependency is tricky.
    // Calling `init` everywhere looks like a smell.
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined && maybeAuthenticatedUser.googleAccessToken !== undefined) {
        return {
            error: false,
            alreadySignedIn: true,
            user: maybeAuthenticatedUser,
        };
    }

    disableCallbacks();

    try {
        const auth2 = await getAuth2();
        if (auth2 === undefined) {
            return {
                kind: "unexpected-error",
                message: "Could not connect to Google.",
            };
        }

        let code: string | undefined;
        let googleUser: gapi.auth2.GoogleUser | undefined;
        if (grantOfflineAccess) {
            // This will throw if the user cancels.
            const result = await (auth2 as gapi.auth2.GoogleAuth).grantOfflineAccess({
                scope: googleDriveScope,
                // The types are busted. You can absolutely combine "consent"
                // and "select_account", as per the OAuth standard.
                prompt: "consent select_account" as any,
            });
            code = result.code;
            googleUser = (auth2 as gapi.auth2.GoogleAuth).currentUser.get();
        }

        if (googleUser === undefined || !googleUser.isSignedIn()) {
            googleUser = await (auth2 as gapi.auth2.GoogleAuth).signIn({
                prompt: grantOfflineAccess ? undefined : "select_account",
            });
        }
        if (!googleUser.isSignedIn()) {
            trackEvent("sign in failed", { reason: "Google user not signed in" });

            await setAuthenticatedUser(undefined);
            return {
                kind: "unexpected-error",
                message: "Sign up did not stick.",
            };
        }

        return {
            error: false,
            alreadySignedIn: false,
            user: googleUser,
            code,
        };
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    } finally {
        enableCallbacks();
    }
}

export async function signInFromGoogleUser(
    googleUser: gapi.auth2.GoogleUser,
    mergeWith: MergeWithExistingUser | undefined
): Promise<AuthenticatedUser | AuthenticationFailure> {
    // Not sure if we have to `init` here. This global dependency is tricky.
    // Calling `init` everywhere looks like a smell.
    init();

    disableCallbacks();

    try {
        const authResponse = googleUser.getAuthResponse(true);
        const googleCredential = firebase.auth.GoogleAuthProvider.credential(
            authResponse.id_token,
            authResponse.access_token
        );
        const userCredential = await firebase.auth().signInWithCredential(googleCredential);

        if (userCredential.user === null) {
            trackEvent("sign in failed", { reason: "Firebase sign in failed" });

            await setAuthenticatedUser(undefined);
            throw new Error("Firebase sign-in resulted in a null user.");
        }

        const maybeGoogleAccessToken = await getAccessTokenForCurrentFirebaseUser(mergeWith);

        if (!isSuccessAccessTokenResponse(maybeGoogleAccessToken)) {
            // We still need to auto-signup if we reach this case.
            // A user could have revoked glide permissions from google,
            // so the email will be registered, but we'll get a `forbidden` response.
            if (maybeGoogleAccessToken.reason === "forbidden") {
                return await signUpWithGoogle(true);
            }

            trackEvent("sign in failed", { reason: "Could not get access token" });

            await firebase.auth().signOut();
            throw new Error("Could not getAccessTokenForCurrentFirebaseUser in signIn");
        }

        // Do not delete this user update. It's there for when people change their names.
        const firebaseUser = userCredential.user;
        const providerData = nullToUndefined(
            firebaseUser.providerData.find(p => {
                const provider = nullToUndefined(p);
                return provider !== undefined && provider.providerId === firebase.auth.GoogleAuthProvider.PROVIDER_ID;
            })
        );
        if (providerData !== undefined) {
            const updates: UserDataUpdates = {
                email: nullToUndefined(providerData.email),
                displayName: nullToUndefined(providerData.displayName ?? providerData.email),
                phoneNumber: nullToUndefined(providerData.phoneNumber),
                photoURL: nullToUndefined(providerData.photoURL),
                integrations: [],
            };
            await saveUserUpdates(updates, {}, undefined, getAppFacilities(), await getSetOrUpdateUsersAppParams());
        }

        trackEvent("sign in success", { provider: "google" });
        trackEvent("verified email", { provider: "google sign in" });

        // for Hubspot
        trackEvent("verified via google sso", {});

        enableCallbacks();

        const userWithToken = {
            firebaseUser,
            googleAccessToken: maybeGoogleAccessToken.accessToken,
        };

        await setAuthenticatedUser(userWithToken);
        return userWithToken;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    } finally {
        enableCallbacks();
    }
}

export function isGoogleUserAlreadySignedIn(
    response:
        | GetGoogleUserSuccessResponse
        | GetGoogleUserSuccessResponseV2
        | AuthenticationFailure
        | GetGoogleUserAlreadySignedIn
): response is GetGoogleUserAlreadySignedIn {
    return (response as GetGoogleUserAlreadySignedIn).alreadySignedIn === true;
}

// We only verify emails in this way if you follow a verification link.
// That is: You signed up with email and later verified.
// Google users are immediately verified by firebase, automatically.
export async function verifyEmail(verifyEmailCode: string): Promise<boolean> {
    init();

    try {
        // This should _not_ happen. We only try to verify email if the user is signed in.
        if (firebase.auth().currentUser === null) {
            logError("No user when trying to verify email");
            return false;
        }

        // You have to first apply the action code,
        // then reload the user,
        // then use the side effect of getIdToken to trigger onIdTokenChanged.
        await firebase.auth().applyActionCode(verifyEmailCode);
        await firebase.auth().currentUser?.reload();
        await firebase.auth().currentUser?.getIdToken(true);

        trackEvent("verified email", { provider: "verification email" });
        // for Hubspot
        trackEvent("verified via email", {});
        void recoverHubspotContactFromWaitingArea();
        return true;
    } catch (err: unknown) {
        logError(err);
        return false;
    }
}

async function recoverHubspotContactFromWaitingArea(): Promise<void> {
    try {
        // We have to drain out the body, otherwise we'll just leave the connection
        // around forever.
        await getAppFacilities()
            .callAuthCloudFunction("recoverHubspotContactFromWaitingArea", {})
            .then(r => r?.text());
    } catch (err: unknown) {
        logError("Count not recover hubspot contact from waiting area", err);
        return;
    }
}

export async function signInFromMagicLink(magicLink: string): Promise<AuthenticatedUser | AuthenticationFailure> {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    try {
        if (!gotFirstAuthStateChanged) {
            throw new Error("Trying to sign in before first Firebase Auth change in signInEmail");
        }

        disableCallbacks();

        trackEvent("sign in start", { provider: "email" });

        const magicURL = new URL(magicLink);
        const queryStringParsed = queryString.parse(magicURL.search.substring(1));
        const { email } = queryStringParsed;
        const emailAddress = checkString(email);

        if (emailAddress === undefined) {
            enableCallbacks();
            throw new Error("URL doesn't include email address");
        }

        if (!firebase.auth().isSignInWithEmailLink(magicLink)) {
            enableCallbacks();
            throw new Error("Sign in link is not valid");
        }

        const userCredential = await firebase.auth().signInWithEmailLink(emailAddress, magicLink);

        if (userCredential.user === null) {
            trackEvent("sign in failed", { reason: "Firebase sign in failed" });

            enableCallbacks();
            await setAuthenticatedUser(undefined);
            throw new Error("Firebase sign-in resulted in a null user.");
        }

        const firebaseUser = userCredential.user;
        if (firebaseUser === null) {
            trackEvent("sign up failed", { reason: "Firebase sign in failed" });
            logError("Could not sign in with Firebase");

            enableCallbacks();
            throw new Error("Firebase sign-up resulted in a null user.");
        }

        const user = { firebaseUser, googleAccessToken: undefined };

        logInfo("Authenticated user", user);

        // We don't have to save anything in particular
        // but the call to set-or-update-user runs a few necessary side effects,
        // like recovering a hubspot contact if the user verified with this sign in.
        await saveUserUpdates({}, {}, undefined, getAppFacilities(), await getSetOrUpdateUsersAppParams());

        trackEvent("sign in success", { provider: "email" });
        trackEvent("verified email", { provider: "sign in magic link" });
        // for Hubspot
        trackEvent("verified via magic link", {});
        enableCallbacks();
        await setAuthenticatedUser(user);
        return user;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

export async function setAuthPersistence(persistent: boolean) {
    init();

    await firebase
        .auth()
        .setPersistence(persistent ? firebase.auth.Auth.Persistence.LOCAL : firebase.auth.Auth.Persistence.SESSION);
}

export async function signUpWithGoogle(viaSignIn: boolean = false): Promise<AuthenticatedUser | AuthenticationFailure> {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    // For brand new users, we're not sure if they're going to consent to
    // the tracking cookies, so we _have_ to shut off Firebase Auth persistence.
    // If we want it back then we'll set it appropriately in the auth state
    // callback of loadOrCreateUser.
    await setAuthPersistence(false);

    // Note that handleSignInSignUpError() turns these into an "unexpected-error" failure.
    // We are throwing these exceptions primarily to pick them up in Sentry.
    try {
        if (!gotFirstAuthStateChanged) {
            throw new Error("Trying to sign in before first Firebase Auth change in signUp");
        }
        disableCallbacks();

        const auth2 = await getAuth2();
        if (auth2 === undefined) return { kind: "no-google-libraries" };

        trackEvent("sign up started", { provider: "google" });

        // This will throw if the user cancels.
        const { code } = await (auth2 as gapi.auth2.GoogleAuth).grantOfflineAccess({
            scope: googleDriveScope,
            // The types are busted. You can absolutely combine "consent"
            // and "select_account", as per the OAuth standard.
            prompt: viaSignIn ? "consent" : ("consent select_account" as any),
            login_hint: viaSignIn ? (auth2 as gapi.auth2.GoogleAuth).currentUser?.get()?.getId() : undefined,
        } as any);

        const body: GetOAuth2TokensForGoogleSheetsBody = { code };
        const response = await getAppFacilities().callAuthIfAvailableCloudFunction(
            "getOAuth2TokensForGoogleSheets",
            body,
            {}
        );
        if (!isResponseOK(response)) {
            // We have to drain out the body, otherwise we'll just leave the connection
            // around forever.
            void response?.text();
            trackEvent("sign up failed", { reason: "Could not get OAuth2 tokens" });
            logError("Could not get tokens for code", response);

            enableCallbacks();
            throw new Error(`Could not getOAuth2TokensForGoogleSheets: ${response?.statusText}`);
        }

        // emailAppendAuthorization could be undefined, and that's OK.
        const { idToken, accessToken, refreshToken, emailAppendAuthorization } = await response.json();
        logInfo("OAuth2 tokens", idToken, accessToken);

        const credential = firebase.auth.GoogleAuthProvider.credential(idToken, accessToken);
        const firebaseCredential = await firebase.auth().signInWithCredential(credential);

        const firebaseUser = firebaseCredential.user;
        if (firebaseUser === null) {
            trackEvent("sign up failed", { reason: "Firebase sign in failed" });
            logError("Could not sign in with Firebase");

            enableCallbacks();
            throw new Error("Firebase sign-up resulted in a null user.");
        }

        const updates: UserDataUpdates = {
            email: nullToUndefined(firebaseUser.email),
            displayName: nullToUndefined(firebaseUser.displayName),
            phoneNumber: nullToUndefined(firebaseUser.phoneNumber),
            photoURL: nullToUndefined(firebaseUser.photoURL),
            flags: {},
            integrations: [
                {
                    name: "google-drive",
                    kind: "google-drive",
                    authentication: {
                        kind: "oauth2-offline",
                        idToken,
                        refreshToken,
                    },
                },
            ],
        };

        const user = { firebaseUser, googleAccessToken: accessToken };

        if (
            await saveUserUpdates(
                updates,
                {},
                emailAppendAuthorization,
                getAppFacilities(),
                await getSetOrUpdateUsersAppParams()
            )
        ) {
            trackEvent("sign up success", { user_id: firebaseUser.uid, referrer: undefined, provider: "google" });
        }
        // for Hubspot
        trackEvent("verified via google sso", {});
        trackEvent("verified email", { provider: "google sign up" });
        logInfo("Authenticated user", user);

        enableCallbacks();
        await setAuthenticatedUser(user);
        return user;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

export async function signUpUnverified(emailAddress: string) {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    // For brand new users, we're not sure if they're going to consent to
    // the tracking cookies, so we _have_ to shut off Firebase Auth persistence.
    // If we want it back then we'll set it appropriately in the auth state
    // callback of loadOrCreateUser.
    await setAuthPersistence(false);

    trackEvent("sign up started", { provider: "email" });

    try {
        disableCallbacks();

        const userCredential = await firebase.auth().signInAnonymously();

        const firebaseUser = userCredential.user;
        if (firebaseUser === null) {
            trackEvent("sign up failed", { reason: "Firebase sign in failed" });
            logError("Could not sign in with Firebase");

            enableCallbacks();
            throw new Error("Firebase sign-up resulted in a null user.");
        }

        await firebaseUser.updateEmail(emailAddress);

        const updates: UserDataUpdates = {
            email: emailAddress,
            displayName: emailAddress,
            flags: {},
            integrations: [],
        };

        const user = { firebaseUser, googleAccessToken: undefined };

        const wasCreated = await saveUserUpdates(
            updates,
            {},
            undefined,
            getAppFacilities(),
            await getSetOrUpdateUsersAppParams()
        );
        if (wasCreated) {
            trackEvent("sign up success", { user_id: firebaseUser.uid, referrer: undefined, provider: "email" });
        }
        logInfo("Authenticated user", user);

        trackEvent("sign in success", { provider: "email" });

        const result = await sendEmailVerification(emailAddress); // TODO can we do this in the background to speed this up?
        // We have to drain out the body, otherwise we'll just leave the connection
        // around forever.
        void result?.text();
        if (result === undefined || result.status !== 200) {
            logError("Failed to send email verification.");
        }

        enableCallbacks();
        await setAuthenticatedUser(user);
        return user;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

/**
 * If you're already signed in, this returns the current authenticated user.
 * If something goes wrong, this returns an AuthenticationFailure.
 * Else, if the magic link email was sent successfully, we return `true`.
 * Also, if you're running on the emulators, we'll return the login link directly.
 */
export async function signInWithMagicLink(
    emailAddress: string
): Promise<AuthenticatedUser | AuthenticationFailure | true | string> {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    // For brand new users, we're not sure if they're going to consent to
    // the tracking cookies, so we _have_ to shut off Firebase Auth persistence.
    // If we want it back then we'll set it appropriately in the auth state
    // callback of loadOrCreateUser.
    await setAuthPersistence(false);

    // Note that handleSignInSignUpError() turns these into an "unexpected-error" failure.
    // We are throwing these exceptions primarily to pick them up in Sentry.
    try {
        if (!gotFirstAuthStateChanged) {
            throw new Error("Trying to sign in before first Firebase Auth change in signUp");
        }
        disableCallbacks();

        trackEvent("sign in start", { provider: "email" });

        const result = await createAndSendAuthLink(emailAddress);
        if (result === undefined) {
            enableCallbacks();
            throw new Error("Failed to create and send auth link.");
        } else if (result.status !== 200) {
            const message = await result.text();
            enableCallbacks();
            throw new Error(`Failed to create and send auth link: ${message}`);
        }

        trackEvent("sign in link sent", {});

        enableCallbacks();

        if (isRunningOnEmulators()) {
            const link = await result.text();
            return link;
        }
        return true;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

export async function isEmailSignedUp(email: string): Promise<boolean> {
    const body: IsEmailSignedUpBody = { email };
    const response = await callCloudFunctionWeb("isEmailSignedUp", body);

    if (response === undefined) {
        throw new Error("Failed to check if the account exists");
    }

    const { isSignedUp } = await response.json();
    return isSignedUp;
}

async function createAndSendAuthLink(emailAddress: string) {
    return callCloudFunctionWeb("createAndSendAuthLink", {
        emailAddress,
        targetURL: window.location.href,
    });
}

async function sendEmailVerification(emailAddress: string) {
    try {
        return getAppFacilities().callAuthCloudFunction("sendEmailVerification", {
            emailAddress,
        });
    } catch (err: unknown) {
        logError(err);
        return undefined;
    }
}

export async function sendVerificationEmailForCurrentUser() {
    const firebaseUser = firebase.auth().currentUser;

    if (firebaseUser === null) {
        return;
    }

    const { email, emailVerified } = firebaseUser;

    if (emailVerified || email === null) {
        return;
    }

    return sendEmailVerification(email);
}

export async function sendVerificationEmailForCurrentUserAndEndConnection() {
    // We have to drain out the body, otherwise we'll just leave the connection
    // around forever.
    await sendVerificationEmailForCurrentUser().then(response => response?.text());
}

export function isThirdPartyCookieError(err: unknown) {
    return (
        hasOwnProperty(err, "error") &&
        hasOwnProperty(err, "details") &&
        // As of April 20, 2022, details === "R" means that cookies aren't enabled.
        // Thanks Google.
        err.error === "idpiframe_initialization_failed" &&
        (err.details === "Cookies are not enabled in current environment." || err.details === "R")
    );
}

function handleSignInSignUpError(err: unknown): AuthenticationFailure {
    enableCallbacks();
    let closeByUser = false;
    let popupBlocked = false;
    let expired = false;
    if (hasOwnProperty(err, "code")) {
        if (err.code === "auth/popup-closed-by-user") {
            closeByUser = true;
        } else if (err.code === "auth/popup-blocked-by-browser") {
            popupBlocked = true;
        } else if (err.code === "auth/invalid-action-code") {
            expired = true;
        }
    }
    if (hasOwnProperty(err, "error")) {
        // access_denied means we asked for drive permissions (during sign up) and the user clicked on Cancel.
        // https://developers.google.com/identity/sign-in/web/reference
        if (err.error === "popup_closed_by_user" || err.error === "access_denied") {
            closeByUser = true;
        } else if (err.error === "popup_blocked_by_browser") {
            popupBlocked = true;
        }
    }
    const cookiesBlocked = isThirdPartyCookieError(err);
    if (closeByUser) {
        trackEvent("sign in closed by user", {});
        return { kind: "declined-sign-in" };
    } else if (popupBlocked) {
        trackEvent("sign in blocked by popup blocker", {});
        return { kind: "popup-blocked" };
    } else if (cookiesBlocked) {
        // There's no point in sending analytics for this: we're in
        // a "high security" environment and they're likely already blocked.
        return { kind: "cookies-blocked" };
    } else if (expired) {
        return { kind: "link-expired" };
    } else {
        trackEvent("sign in failed", { reason: "Exception thrown" });
    }
    logError("error signing in", err);
    // Well, we don't actually know what the error is.
    // If it's a proper Error, we can get a message out of it.
    // But Google's libraries are nasty and will throw non-Errors.
    // So we need to fall back to this weird thing.
    //
    // You couldn't sign in, so you're already having a bad day.
    // At least this JSON will give us an idea of what went wrong.
    const message = err instanceof Error ? err.message : JSON.stringify(err, undefined, 4);
    return { kind: "unexpected-error", message };
}

export async function signOut(): Promise<void> {
    logInfo("signing out");

    if (!(await getFeatureSettingStable("useGoogleIdentityServices"))) {
        const auth2 = await getAuth2();
        if (auth2 !== undefined && auth2.isSignedIn.get()) {
            auth2.signOut();
        }
    }

    await setAuthPersistence(false);
    await firebase.auth().signOut();
}

export enum GoogleAccessTokenFailureKind {
    UserCanceled = "canceled",
    SigInFailure = "sign-in-failure", // comes with an `AuthenticationFailure`
    NoAccess = "no-access",
}
